import axios from 'axios'
import { useEffect } from 'react'
import { appDispatch } from '../..'
import { logout, StorageConfiguration } from '../../slices/auth-slice'
import jwt_decode, { JwtPayload } from 'jwt-decode'

interface DecodedJwtToken {
  role: string | undefined
  permissions: string[] | undefined
}

// ─── Register interceptors at module load time ────────────────────────────
// This runs once when the module is first imported, BEFORE any React component
// renders. Ensures the Authorization header is attached to every request
// including those fired during the initial mount cycle.

axios.interceptors.request.use(
  function (config) {
    const token = localStorage.getItem(StorageConfiguration.tokenStorageTag)
    if (token) {
      config.headers.set('authorization', `Bearer ${token}`)
    }
    return config
  },
  function (error) {
    return Promise.reject(error)
  }
)

axios.interceptors.response.use(
  response => response,
  error => {
    const message = error?.response?.data?.message
    const token = localStorage.getItem(StorageConfiguration.tokenStorageTag)
    if (!token) return Promise.reject(error)

    try {
      const decodedToken = jwt_decode<JwtPayload>(token)
      const decodedDate = decodedToken.exp ?? 0
      const currentDate = Date.now() / 1000
      if ((message && message.includes('JWT')) || currentDate > decodedDate) {
        appDispatch(logout())
      }
    } catch {
      appDispatch(logout())
    }

    return Promise.reject(error)
  }
)

// ─── React hook for initial token validation ──────────────────────────────
// Validates the stored token on mount. If expired or corrupted, logs out.

export const useAuthJWT = (): void => {
  useEffect(() => {
    const currentToken = localStorage.getItem(StorageConfiguration.tokenStorageTag)
    if (currentToken != null) {
      try {
        const decodedToken = jwt_decode<DecodedJwtToken & JwtPayload>(currentToken)
        const isExpired = (decodedToken.exp ?? 0) < Date.now() / 1000
        if (!decodedToken.permissions || isExpired) {
          appDispatch(logout())
        }
      } catch {
        appDispatch(logout())
      }
    }
  }, [])
}
